import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowRight, Globe2, ShieldCheck, Boxes, LineChart, Handshake, Truck,
  Search, FileText, BadgeCheck, Menu, X, Mail, Phone, MapPin,
} from "lucide-react";
import { toast } from "sonner";
import Logo from "@/components/Logo";
import api, { formatApiErrorDetail } from "@/lib/api";

const HERO = "https://images.unsplash.com/photo-1574848296471-28f79a036f79?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1Mjh8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBvZmZpY2UlMjBidWlsZGluZyUyMGV4dGVyaW9yJTIwZ2VvbWV0cmljJTIwYXJjaGl0ZWN0dXJlfGVufDB8fHx8MTc4MjA3OTQ1N3ww&ixlib=rb-4.1.0&q=85";
const SHIP = "https://images.unsplash.com/photo-1496347326319-2935d381b307?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1OTV8MHwxfHNlYXJjaHw0fHxjb3Jwb3JhdGUlMjBnbG9iYWwlMjBzaGlwcGluZyUyMGNhcmdvJTIwc2hpcCUyMHNoaXAlMjBzdW5zZXR8ZW58MHx8fHwxNzgyMDc5NDU3fDA&ixlib=rb-4.1.0&q=85";
const HANDSHAKE = "https://images.unsplash.com/photo-1758518730384-be3d205838e8?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTB8MHwxfHNlYXJjaHwxfHxidXNpbmVzcyUyMGhhbmRzaGFrZSUyMHByb2Zlc3Npb25hbCUyMG9mZmljZXxlbnwwfHx8fDE3ODIwNzk0NTd8MA&ixlib=rb-4.1.0&q=85";

const services = [
  { icon: Search, title: "Strategic Sourcing", desc: "We identify, vet, and negotiate with the right suppliers worldwide to secure quality at the best total cost." },
  { icon: Boxes, title: "Procurement Management", desc: "End-to-end procurement execution — POs, logistics coordination, QC, and on-time delivery, handled for you." },
  { icon: Handshake, title: "Supplier Management", desc: "Ongoing supplier relationships, performance scorecards, and risk mitigation across your supply base." },
  { icon: LineChart, title: "Cost Optimization", desc: "Spend analysis and category strategy that turns procurement into a measurable margin advantage." },
  { icon: Globe2, title: "Global Trade Support", desc: "International logistics, customs, and compliance guidance for cross-border sourcing in 135+ markets." },
  { icon: ShieldCheck, title: "Quality & Compliance", desc: "Factory audits, inspections, and compliance checks so every shipment meets your standards." },
];

const steps = [
  { n: "01", icon: FileText, title: "Submit your RFQ", desc: "Tell us what you need — product details, volumes, target pricing." },
  { n: "02", icon: Search, title: "We source & vet", desc: "Our team matches you with qualified suppliers and negotiates terms." },
  { n: "03", icon: BadgeCheck, title: "Review your quote", desc: "Get a transparent quote in your portal. Accept with one click." },
  { n: "04", icon: Truck, title: "Confirm & track", desc: "Approve your quote and track production and delivery to your door." },
];

const stats = [
  { v: "135+", l: "Markets sourced" },
  { v: "$40M+", l: "Spend managed" },
  { v: "18%", l: "Avg. cost reduction" },
  { v: "24h", l: "Quote turnaround" },
];

function Nav() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  const links = [
    ["Services", "#services"],
    ["Process", "#process"],
    ["About", "#about"],
    ["Contact", "#contact"],
  ];
  return (
    <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? "bg-white/90 backdrop-blur-md border-b border-zinc-200" : "bg-transparent"}`}>
      <nav className="mx-auto max-w-7xl px-6 md:px-12 flex items-center justify-between h-20">
        <Logo light size="h-10" />
        <div className="hidden md:flex items-center gap-8">
          {links.map(([label, href]) => (
            <a key={label} href={href} className="text-sm font-semibold text-zinc-700 hover:text-coral transition-colors" data-testid={`nav-${label.toLowerCase()}`}>
              {label}
            </a>
          ))}
          <Link to="/login" className="text-sm font-semibold text-zinc-700 hover:text-coral transition-colors" data-testid="nav-login">Sign in</Link>
          <a href="#contact" className="bg-coral hover:bg-coral-hover text-white text-sm font-semibold px-5 py-2.5 rounded-sm transition-colors" data-testid="nav-cta">
            Request a Quote
          </a>
        </div>
        <button className="md:hidden text-ink" onClick={() => setOpen(!open)} data-testid="nav-mobile-toggle">
          {open ? <X /> : <Menu />}
        </button>
      </nav>
      {open && (
        <div className="md:hidden bg-white border-b border-zinc-200 px-6 py-4 flex flex-col gap-4">
          {links.map(([label, href]) => (
            <a key={label} href={href} onClick={() => setOpen(false)} className="text-sm font-semibold text-zinc-700">{label}</a>
          ))}
          <Link to="/login" className="text-sm font-semibold text-zinc-700">Sign in</Link>
          <a href="#contact" onClick={() => setOpen(false)} className="bg-coral text-white text-center text-sm font-semibold px-5 py-2.5 rounded-sm">Request a Quote</a>
        </div>
      )}
    </header>
  );
}

function InquiryForm() {
  const [form, setForm] = useState({ name: "", email: "", company: "", phone: "", message: "" });
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/inquiries", form);
      toast.success("Inquiry sent! Our team will reach out within 24 hours.");
      setForm({ name: "", email: "", company: "", phone: "", message: "" });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to send inquiry");
    } finally {
      setLoading(false);
    }
  };
  const inp = "w-full bg-white border border-zinc-300 rounded-sm px-4 py-3 text-sm focus:border-coral focus:ring-1 focus:ring-coral outline-none transition";
  return (
    <form onSubmit={submit} className="space-y-4" data-testid="inquiry-form">
      <div className="grid sm:grid-cols-2 gap-4">
        <input required placeholder="Full name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inp} data-testid="inquiry-name" />
        <input required type="email" placeholder="Work email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={inp} data-testid="inquiry-email" />
        <input placeholder="Company" value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} className={inp} data-testid="inquiry-company" />
        <input placeholder="Phone (optional)" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className={inp} data-testid="inquiry-phone" />
      </div>
      <textarea required rows={4} placeholder="What are you looking to source?" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className={inp} data-testid="inquiry-message" />
      <button disabled={loading} className="w-full bg-coral hover:bg-coral-hover text-white font-semibold py-3.5 rounded-sm transition-colors disabled:opacity-60 flex items-center justify-center gap-2" data-testid="inquiry-submit">
        {loading ? "Sending..." : "Send Inquiry"} <ArrowRight size={18} />
      </button>
    </form>
  );
}

export default function Landing() {
  return (
    <div className="bg-background text-ink overflow-x-hidden">
      <Nav />

      {/* HERO */}
      <section className="relative min-h-screen flex items-center">
        <div className="absolute inset-0">
          <img src={HERO} alt="" className="h-full w-full object-cover" />
          <div className="absolute inset-0 bg-ink/70" />
          <div className="absolute inset-0 bg-gradient-to-r from-ink/90 via-ink/50 to-transparent" />
        </div>
        <div className="relative mx-auto max-w-7xl px-6 md:px-12 pt-32 pb-20 w-full">
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }} className="max-w-3xl">
            <div className="inline-flex items-center gap-2 border border-white/20 bg-white/5 backdrop-blur px-4 py-1.5 rounded-full text-white/80 text-xs font-semibold uppercase tracking-widest mb-8">
              <span className="h-1.5 w-1.5 rounded-full bg-coral" /> US-based · Serving clients worldwide
            </div>
            <h1 className="font-serif text-5xl md:text-7xl font-bold text-white leading-[1.05] tracking-tight text-balance">
              Strategic sourcing that <span className="text-coral italic">moves margins.</span>
            </h1>
            <p className="mt-6 text-lg text-white/70 max-w-xl leading-relaxed">
              NP Strategy Sourcing Inc connects you to vetted global suppliers, negotiates on your behalf, and manages procurement end-to-end — so you buy better, faster, and with confidence.
            </p>
            <div className="mt-10 flex flex-col sm:flex-row gap-4">
              <a href="#contact" className="bg-coral hover:bg-coral-hover text-white font-semibold px-8 py-4 rounded-sm transition-colors flex items-center justify-center gap-2" data-testid="hero-cta-button">
                Request a Quote <ArrowRight size={18} />
              </a>
              <Link to="/register" className="border border-white/30 text-white hover:bg-white/10 font-semibold px-8 py-4 rounded-sm transition-colors text-center" data-testid="hero-portal-button">
                Open Client Portal
              </Link>
            </div>
          </motion.div>
        </div>
        <div className="absolute bottom-0 left-0 right-0 bg-ink/80 backdrop-blur border-t border-white/10">
          <div className="mx-auto max-w-7xl px-6 md:px-12 grid grid-cols-2 md:grid-cols-4 divide-x divide-white/10">
            {stats.map((s) => (
              <div key={s.l} className="py-6 px-4 text-center md:text-left">
                <div className="font-serif text-3xl font-bold text-white">{s.v}</div>
                <div className="text-xs uppercase tracking-wider text-white/50 mt-1">{s.l}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SERVICES */}
      <section id="services" className="py-24 md:py-32 bg-background">
        <div className="mx-auto max-w-7xl px-6 md:px-12">
          <div className="max-w-2xl mb-16">
            <p className="text-coral text-sm font-bold uppercase tracking-widest mb-4">Capabilities</p>
            <h2 className="font-serif text-4xl md:text-5xl font-bold tracking-tight">A complete sourcing partner, not just a broker.</h2>
            <p className="mt-5 text-zinc-600 text-lg">From first RFQ to final delivery, we own the hard parts of global procurement.</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-px bg-zinc-200 border border-zinc-200">
            {services.map((s, i) => (
              <motion.div
                key={s.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: (i % 3) * 0.08 }}
                className="group bg-white p-8 hover:bg-ink transition-colors duration-300"
                data-testid={`service-card-${i}`}
              >
                <s.icon className="text-coral mb-6" size={32} strokeWidth={1.5} />
                <h3 className="font-serif text-xl font-bold mb-3 group-hover:text-white transition-colors">{s.title}</h3>
                <p className="text-sm text-zinc-600 leading-relaxed group-hover:text-white/60 transition-colors">{s.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ABOUT */}
      <section id="about" className="py-24 md:py-32 bg-white">
        <div className="mx-auto max-w-7xl px-6 md:px-12 grid lg:grid-cols-2 gap-16 items-center">
          <div className="relative">
            <img src={SHIP} alt="Global shipping" className="w-full h-[480px] object-cover rounded-sm" />
            <div className="absolute -bottom-8 -right-4 md:right-8 bg-coral text-white p-8 max-w-xs rounded-sm hidden sm:block">
              <div className="font-serif text-4xl font-bold">Since 2014</div>
              <p className="text-sm text-white/80 mt-2">Trusted by importers, manufacturers, and brands across the Americas, Europe & Asia.</p>
            </div>
          </div>
          <div>
            <p className="text-coral text-sm font-bold uppercase tracking-widest mb-4">Why NPI</p>
            <h2 className="font-serif text-4xl md:text-5xl font-bold tracking-tight leading-tight">We act like your in-house procurement team.</h2>
            <p className="mt-6 text-zinc-600 text-lg leading-relaxed">
              Most buyers waste weeks chasing suppliers, comparing quotes, and managing risk. We compress that into a single, accountable partner — with US-based oversight and boots-on-the-ground sourcing globally.
            </p>
            <ul className="mt-8 space-y-4">
              {[
                "Dedicated sourcing specialists for every account",
                "Transparent quoting — no hidden markups",
                "Direct communication via email and WhatsApp",
                "Real-time status tracking in your client portal",
              ].map((t) => (
                <li key={t} className="flex items-start gap-3">
                  <BadgeCheck className="text-coral mt-0.5 shrink-0" size={20} />
                  <span className="text-zinc-700">{t}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* PROCESS */}
      <section id="process" className="py-24 md:py-32 bg-ink text-white">
        <div className="mx-auto max-w-7xl px-6 md:px-12">
          <div className="max-w-2xl mb-16">
            <p className="text-coral text-sm font-bold uppercase tracking-widest mb-4">How it works</p>
            <h2 className="font-serif text-4xl md:text-5xl font-bold tracking-tight text-white">From inquiry to delivery in four steps.</h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {steps.map((s, i) => (
              <motion.div
                key={s.n}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                className="border-t-2 border-coral pt-6"
                data-testid={`process-step-${i}`}
              >
                <div className="flex items-center justify-between mb-6">
                  <span className="font-serif text-5xl font-bold text-white/15">{s.n}</span>
                  <s.icon className="text-coral" size={28} strokeWidth={1.5} />
                </div>
                <h3 className="font-serif text-xl font-bold mb-2 text-white">{s.title}</h3>
                <p className="text-sm text-white/60 leading-relaxed">{s.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* TESTIMONIAL */}
      <section className="py-24 bg-background">
        <div className="mx-auto max-w-5xl px-6 md:px-12 text-center">
          <img src={HANDSHAKE} alt="" className="w-20 h-20 object-cover rounded-full mx-auto mb-8" />
          <blockquote className="font-serif text-2xl md:text-4xl font-medium leading-snug tracking-tight text-balance">
            "NPI Strategic Sourcing became an extension of our team. They cut our landed costs by <span className="text-coral">21%</span> and took supplier headaches off our plate entirely."
          </blockquote>
          <div className="mt-8">
            <div className="font-semibold">Daniela R.</div>
            <div className="text-sm text-zinc-500">VP Operations · Consumer Goods Importer</div>
          </div>
        </div>
      </section>

      {/* CONTACT */}
      <section id="contact" className="py-24 md:py-32 bg-white">
        <div className="mx-auto max-w-7xl px-6 md:px-12 grid lg:grid-cols-2 gap-16">
          <div>
            <p className="text-coral text-sm font-bold uppercase tracking-widest mb-4">Get started</p>
            <h2 className="font-serif text-4xl md:text-5xl font-bold tracking-tight leading-tight">Make an inquiry or request a quote.</h2>
            <p className="mt-5 text-zinc-600 text-lg">Tell us what you need to source. A specialist will respond within 24 hours via email or WhatsApp.</p>
            <div className="mt-10 space-y-5">
              <div className="flex items-center gap-4"><Mail className="text-coral" size={20} /><span className="text-zinc-700">info@npstrategyandsourcing.com</span></div>
              <div className="flex items-center gap-4"><Phone className="text-coral" size={20} /><span className="text-zinc-700">+1 (302) 555-0142 · WhatsApp available</span></div>
              <div className="flex items-center gap-4"><MapPin className="text-coral" size={20} /><span className="text-zinc-700">Wilmington, Delaware, USA</span></div>
            </div>
          </div>
          <div className="bg-background border border-zinc-200 p-8 rounded-sm">
            <InquiryForm />
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="bg-ink text-white/70 py-14">
        <div className="mx-auto max-w-7xl px-6 md:px-12 flex flex-col md:flex-row justify-between items-start gap-8">
          <div className="max-w-sm">
            <Logo light />
            <p className="mt-4 text-sm text-white/50">Strategic sourcing & procurement for businesses that buy globally. US-based, worldwide reach.</p>
          </div>
          <div className="flex gap-16">
            <div className="flex flex-col gap-3 text-sm">
              <span className="text-white/40 uppercase text-xs tracking-widest mb-1">Company</span>
              <a href="#services" className="hover:text-coral transition">Services</a>
              <a href="#about" className="hover:text-coral transition">About</a>
              <a href="#contact" className="hover:text-coral transition">Contact</a>
            </div>
            <div className="flex flex-col gap-3 text-sm">
              <span className="text-white/40 uppercase text-xs tracking-widest mb-1">Portal</span>
              <Link to="/login" className="hover:text-coral transition">Sign in</Link>
              <Link to="/register" className="hover:text-coral transition">Create account</Link>
            </div>
          </div>
        </div>
        <div className="mx-auto max-w-7xl px-6 md:px-12 mt-12 pt-6 border-t border-white/10 text-xs text-white/40">
          © {new Date().getFullYear()} NPI Strategic Sourcing Inc. All rights reserved.
        </div>
      </footer>
    </div>
  );
}
